import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { nextActionsApi, type NextActionOptionDto } from '@/shared/api/nextActionsApi';
import { Button } from '@/shared/components/Button';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { ApiError } from '@/shared/api/httpClient';

export function SettingsPage() {
  const { user } = useAuth();
  const exhibitorId = user?.exhibitorId;

  const [options, setOptions] = useState<NextActionOptionDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  function refetch() {
    if (!exhibitorId) return;
    setIsLoading(true);
    nextActionsApi.listByExhibitor(exhibitorId).then(setOptions).finally(() => setIsLoading(false));
  }

  useEffect(refetch, [exhibitorId]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!exhibitorId || !newLabel.trim()) return;
    setError(null);
    setIsSaving(true);
    try {
      await nextActionsApi.create(exhibitorId, newLabel.trim());
      setNewLabel('');
      refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not add option.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRename(id: string) {
    if (!editLabel.trim()) return;
    setError(null);
    setIsSaving(true);
    try {
      await nextActionsApi.update(id, editLabel.trim());
      setEditingId(null);
      refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not save changes.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Remove this option? Leads that already used it will keep showing it.')) return;
    await nextActionsApi.delete(id);
    refetch();
  }

  if (!exhibitorId) {
    return <EmptyState icon="⚙" title="No exhibitor on your account" />;
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-7)' }}>
        <Spinner label="Loading settings" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-display" style={{ fontSize: '1.375rem', marginBottom: 'var(--space-2)' }}>
        Settings
      </h1>
      <h2 className="font-display" style={{ fontSize: '1.0625rem', marginBottom: 'var(--space-1)' }}>
        Next Action options
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: 0, marginBottom: 'var(--space-4)' }}>
        These appear in the "Next Action" dropdown when your team edits a captured lead.
      </p>

      {error && <div className="error-banner" style={{ marginBottom: 'var(--space-4)' }}>{error}</div>}

      {options.length === 0 ? (
        <EmptyState icon="⚙" title="No options yet" description="Add your first one below." />
      ) : (
        options.map((opt) => (
          <div
            key={opt.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3) var(--space-4)',
              marginBottom: 'var(--space-2)'
            }}
          >
            {editingId === opt.id ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', width: '100%' }}>
                <input
                  className="field__input"
                  style={{ flex: '1 1 160px', minWidth: 0 }}
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                  autoFocus
                />
                <Button onClick={() => void handleRename(opt.id)} isLoading={isSaving}>
                  Save
                </Button>
                <button
                  type="button"
                  onClick={() => setEditingId(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <span style={{ flex: 1 }}>{opt.label}</span>
                  <button
                  type="button"
                  onClick={() => {
                    setEditingId(opt.id);
                    setEditLabel(opt.label);
                  }}
                  aria-label={`Edit ${opt.label}`}
                  title="Edit"
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '1rem', minWidth: 44, minHeight: 44 }}
                >
                  ✎
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(opt.id)}
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '0.875rem' }}
                >
                  Remove
                </button>
              </>
            )}
          </div>
        ))
      )}

      <form onSubmit={handleAdd} style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
        <input
          className="field__input"
          style={{ flex: '1 1 160px', minWidth: 0 }}
          placeholder="Add a new option"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
        />
        <Button type="submit" isLoading={isSaving} disabled={!newLabel.trim()}>
          Add
        </Button>
      </form>
    </div>
  );
}